import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import OptionButton from '@/components/common/OptionButton/OptionButton';
import EditIcon from '@/components/common/OptionButton/icons/Edit/EditIcon';
import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
import DnrContractReasonModal from './components/DnrContractReasonModal';
import useDnrContractLines from './hooks/useDnrContractLines';
import DnrBulkUpdateModal from './dnrBulkUpdateModal/DnrBulkUpdateModal';
import { DNR_TYPE } from '@/config/reasonTypes.config';
import { getReasonTypes } from '@/utils/data-helpers/reasonTypes';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import styles from './dnr-container.module.scss';
import {
  useSelectedLines
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { useSelectedVariableLines } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';
import useGetContractLinesFromCache from '@/hooks/contract/useGetContractLinesFromCache.hook';
import { ContractLineBillingCycleOutput, ContractStatusEnum } from '@/graphql/contracts/_generated_/contracts';
import { ContractLineCommonData } from '@/graphql/contracts/_generated_/ContractLineCommonData.fragment';

export default function DnrStatusContainer({ readOnly }: { readOnly?: boolean }) {
  const { contractId }: { contractId: UUID } = useParams();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenBulkModal, setIsOpenBulkModal] = useState(false);
  const { dnrContractLines } = useDnrContractLines();
  const reasonTypes = getReasonTypes();
  const selectedLineRows = useSelectedLines();
  const selectedVariableLineRows = useSelectedVariableLines();
  const selectedLinesCount = selectedLineRows?.length > 0 ? selectedLineRows.length : selectedVariableLineRows?.length;
  const { cachedBCContractLines, cachedVariableContractLines } =
    useGetContractLinesFromCache(contractId);

  const { contract } = useGetContractDetails(contractId);
  const { reasonCodes, isLoading, getReasonCodes } = useGetReasonCodes();

  const selectedDNR = useMemo(() => {
    if (selectedLineRows.length > 0) {
      return cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items
        ?.filter((contractLine) => contractLine?.status == ContractStatusEnum.ActiveDNR &&
          selectedLineRows.some(({ id }) => contractLine.id == id));
    }
    if (selectedVariableLineRows.length > 0 &&
      cachedVariableContractLines?.paginatedFilteredVariableContractLines?.items) {
      return cachedVariableContractLines?.paginatedFilteredVariableContractLines?.items
        .filter((contractLine) => contractLine?.status === ContractStatusEnum.ActiveDNR &&
          selectedVariableLineRows.some(({ id }) => contractLine.iD === id));
    }
    return [];
  }, [cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items,
    cachedVariableContractLines?.paginatedFilteredVariableContractLines?.items,
    selectedVariableLineRows, selectedLineRows
  ]) as unknown as ContractLineCommonData[];

  useEffect(() => {
    if (isOpenModal) {
      getReasonCodes(DNR_TYPE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenModal]);

  useEffect(() => {
    if (isOpenBulkModal) {
      getReasonCodes(DNR_TYPE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenBulkModal]);

  const getLastBillingEndDate = useCallback(() => {
    const selectedIds = selectedLineRows?.length > 0
      ? selectedLineRows.map(({ id }) => id)
      : selectedVariableLineRows?.map(({ id }) => id) ?? [];

    const items = cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items;
    if (!items || selectedIds.length === 0) return null;

    const selectedItems = items.filter(
      (item): item is ContractLineBillingCycleOutput => item !== null && selectedIds.includes(item.id)
    );

    const dates = selectedItems
      .map((item) => item.lastBillingEndDate)
      .filter((d): d is string => d !== null && d !== undefined);

    if (dates.length === 0) return null;

    return dates.reduce((latest, current) =>
      new Date(current) > new Date(latest) ? current : latest
    );
  }, [cachedBCContractLines, selectedLineRows, selectedVariableLineRows]);

  if (contract?.status.status === 'Cancelled' ||
    contract?.status.status === 'Expired' ||
    contract?.status.status === 'Inactive') {
    return null;
  }

  if (readOnly && !(selectedDNR?.length! > 0)) {
    return null;
  }

  return (
    <>
      {isOpenModal && (
        <DnrContractReasonModal
          reasonCodes={reasonCodes}
          reasonTypes={reasonTypes}
          contract={contract}
          dnrContractLines={dnrContractLines}
          onClose={() => setIsOpenModal(false)}
          selectedContractLinesCount={selectedLinesCount}
          isLoadingReasonCodes={isLoading}
          lastBillingEndDate={getLastBillingEndDate()}
          readOnly={readOnly}
        />
      )}

      {isOpenBulkModal && (
        <DnrBulkUpdateModal
          dnrContractLines={selectedDNR}
          onClose={() => setIsOpenBulkModal(false)}
          contract={contract!}
          reasonCodes={reasonCodes}
          reasonTypes={reasonTypes}
          isLoadingReasonCodes={isLoading}
          readOnly={readOnly}
        />
      )
      }

      <div className={styles.checkboxWrapper}>
        <div>
          {selectedDNR?.length! > 0 ? (
            <OptionButton
              Icon={<EditIcon />}
              onClick={() => setIsOpenBulkModal(true)}
            >
              Edit DNR line(s)
            </OptionButton>
          ) : !readOnly ? (
            <OptionButton
              Icon={<EditIcon />}
              onClick={() => setIsOpenModal(true)}
            >
              DNR line(s)
            </OptionButton>
          ) : null}
        </div>
      </div>
    </>
  );
}
